import { Formik, Form } from "formik";
import { FormModel } from "../types/FormTypes";
import { useAtom } from "jotai";
import { formDataAtom } from "../App";
import { useLocation, useNavigate } from "react-router-dom";
import { useRef } from "react";
import useFocusOnLoad from "../hooks/useFocusOnLoad";
import { createJourney } from "../utils/journeyHelpers";
import {
  ErrorList,
  Progressbar,
  Box,
  handleSubmitClick,
  Button,
  Heading,
} from "@ogt/ui";

import JourneySummary from "../components/JourneySummary";

export const ROUND_TRIP = "round_trip";
export const ONEWAY_TRIP = "one_way_trip";

export default function TravelSummary() {
  const h2Ref = useFocusOnLoad<HTMLHeadingElement>();
  const [formData, setFormData] = useAtom(formDataAtom);
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const routerLocation = useLocation();
  const progress = 48;
  const journeys = [
    createJourney(formData.selectedJourney, "Resa dit", "../vart-vill-ni-resa"),
  ];

  if (
    formData.selectedReturnJourney &&
    formData.selectedReturnJourney.fromName
  ) {
    journeys.push(
      createJourney(
        formData.selectedReturnJourney,
        "Resa tillbaka",
        "../valj-aterresa"
      )
    );
  }

  return (
    <div>
      <Formik<FormModel>
        onSubmit={(values) => {
          setFormData({
            ...formData,
            ...values,
          });

          navigate("/kontaktuppgifter");
        }}
        initialValues={{
          ...formData,
        }}
        enableReinitialize
        validateOnChange={false}
        validateOnBlur={false}
      >
        {({ errors, values }) => (
          <>
            <div className="flex justify-end items-center">
              <div className="w-full max-w-xs mb-4">
                <Progressbar percentage={progress} />
              </div>
            </div>
            <Form noValidate>
              <Box>
                <Heading
                  progress={progress}
                  headingRef={h2Ref}
                  focusable
                  id="travelSummaryHeading"
                >
                  Er resa
                </Heading>

                <p className="mb-0">
                  Kolla så att allt stämmer om er resa. Behöver du ändra något
                  är det bara att trycka på “Gå tillbaka och ändra”.
                </p>

                {Object.keys(errors).length > 0 && (
                  <ErrorList errors={errors} />
                )}

                <div className="summary-section patient-summary mb-4 border-0 pb-2">
                  <div className="summary-data-wrapper flex flex-col">
                    {journeys.map((journey, index) => (
                      <JourneySummary
                        key={index}
                        journey={journey}
                        isReturnTravel={journey.Headline.includes("tillbaka")}
                        isLast={index === journeys.length - 1}
                        onEdit={(editPath: string) =>
                          navigate(editPath, {
                            state: { fromSummary: routerLocation.pathname },
                          })
                        }
                        onRemove={() => {
                          setFormData({
                            ...formData,
                            selectedReturnJourney: {
                              fromName: "",
                              toName: "",
                              date: "",
                              travelTime: "",
                              journeyKey: "",
                              line: [],
                              switches: 0,
                              unformattedDate: "",
                            },
                            traveltype: ONEWAY_TRIP,
                          });
                        }}
                      />
                    ))}

                    {!values.selectedReturnJourney.fromName ? (
                      <div className="summary-section patient-summary mb-4 last">
                        <Heading as="h3" className="summary-section-heading">
                          Behöver ni resa tillbaka?
                        </Heading>

                        <p>
                          Vi hjälper er gärna att lägga till en resa tillbaka.
                          Hur ni fyllt i resan dit påverkas inte av detta.
                        </p>

                        <Button
                          variant="ghost"
                          iconName="plus"
                          iconPosition="left"
                          showIcon
                          onClick={() => navigate("/valj-aterresa")}
                        >
                          Lägg till en resa tillbaka
                        </Button>
                      </div>
                    ) : (
                      <></>
                    )}
                  </div>
                </div>
              </Box>

              <div className="flex justify-center">
                <div className="w-full text-left flex items-center">
                  <Button
                    iconPosition="left"
                    iconSize={16}
                    iconName="arrow-left"
                    aria-label="Tillbaka"
                    showIcon
                    size="medium"
                    variant="outline"
                    onClick={(event) => {
                      event.preventDefault();
                      const backUrl = undefined;
                      if (backUrl) {
                        navigate(backUrl, { replace: true });
                      } else {
                        navigate(-1);
                      }
                    }}
                  >
                    Tillbaka
                  </Button>
                </div>
                <div className="w-full text-right">
                  <Button
                    onClick={handleSubmitClick(formRef)}
                    className="btn primary"
                    type="submit"
                  >
                    Gå vidare
                  </Button>
                </div>
              </div>
            </Form>
          </>
        )}
      </Formik>
    </div>
  );
}
